import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Star, Plus } from "lucide-react";
import { toast } from "sonner";
import { inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export function Stars({ rating = 0, className = "w-3.5 h-3.5" }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating} stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${className} ${i <= Math.round(rating) ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
      ))}
    </span>
  );
}

export default function ProductCard({ product, index = 0 }) {
  const { add } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  const quickAdd = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return nav("/login");
    try {
      await add(product.featured_offer_id, 1);
      toast.success("Added to cart");
    } catch (err) {
      toast.error("Could not add to cart");
    }
  };

  return (
    <Link
      to={`/product/${product.id}`}
      data-testid={`product-card-${product.id}`}
      className="product-card group block rounded-xl border border-transparent bg-white stagger-item"
      style={{ animationDelay: `${Math.min(index, 8) * 50}ms` }}
    >
      <div className="relative overflow-hidden rounded-xl bg-slate-100 aspect-square">
        {product.image ? (
          <img src={product.image} alt={product.title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300 text-sm">No image</div>
        )}
        {product.discount_pct > 0 && (
          <span className="absolute top-3 left-3 bg-blue-500 text-white text-[11px] font-bold px-2.5 py-1 rounded-full">
            {product.discount_pct}% off
          </span>
        )}
        {product.featured_offer_id && (
          <button
            data-testid={`quick-add-${product.id}`}
            onClick={quickAdd}
            className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-900 text-white rounded-full px-4 py-2 text-xs font-semibold flex items-center gap-1.5 shadow-2xl"
          >
            <Plus className="w-3.5 h-3.5" /> Quick add
          </button>
        )}
      </div>
      <div className="pt-3 pb-1 px-1">
        <p className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">{product.brand}</p>
        <h3 className="text-sm font-semibold text-slate-900 leading-snug line-clamp-2 mt-0.5">{product.title}</h3>
        <div className="flex items-center gap-1.5 mt-1.5">
          <Stars rating={product.rating} />
          <span className="text-xs text-slate-400">({product.review_count})</span>
        </div>
        <div className="flex items-baseline gap-2 mt-1.5">
          <span className="text-lg font-bold text-slate-900">{inr(product.price)}</span>
          {product.mrp > product.price && (
            <span className="text-xs text-slate-400 line-through">{inr(product.mrp)}</span>
          )}
        </div>
        <p className="text-[11px] text-slate-400 mt-1">
          Sold by {product.seller_name}
          {product.offer_count > 1 && <span className="text-blue-500"> · {product.offer_count} sellers</span>}
        </p>
      </div>
    </Link>
  );
}
